'use client'

import { useEffect, useRef, useState } from 'react'

import { formatTotalScansCounterValue } from '@/utils/formatTotalScansCounterValue'
import { getTotalScansCounterValue } from '@/utils/getTotalScansCounterValue'

type Props = {
	id: string
	initialValue: number
	locale: string
	total: number
	ratePerSecond: number
	countedAt: string
}

const TICK_INTERVAL_MS = 3000
const ANIMATION_DURATION_MS = 800
const MAX_ANIMATED_GAP_IN_TICKS = 2

const getInitialRawValue = (
	id: string,
	initialValue: number,
	total: number,
	ratePerSecond: number,
	countedAt: string,
): number => {
	if (typeof document === 'undefined') return initialValue
	const rawValueAttr = document.getElementById(id)?.dataset.rawValue
	return rawValueAttr
		? Number(rawValueAttr)
		: getTotalScansCounterValue(total, ratePerSecond, countedAt)
}

export const TotalScansCounterValue = ({
	id,
	initialValue,
	locale,
	total,
	ratePerSecond,
	countedAt,
}: Props) => {
	const currentValueRef = useRef(
		getInitialRawValue(id, initialValue, total, ratePerSecond, countedAt),
	)
	const [displayValue, setDisplayValue] = useState(() =>
		formatTotalScansCounterValue(currentValueRef.current, locale),
	)

	useEffect(() => {
		let animationFrameId = 0

		const maxAnimatedGap =
			ratePerSecond *
			(TICK_INTERVAL_MS / 1000) *
			MAX_ANIMATED_GAP_IN_TICKS

		const animateTo = (targetValue: number) => {
			cancelAnimationFrame(animationFrameId)

			const startValue = currentValueRef.current

			if (Math.abs(targetValue - startValue) > maxAnimatedGap) {
				currentValueRef.current = targetValue
				setDisplayValue(
					formatTotalScansCounterValue(targetValue, locale),
				)
				return
			}

			const startTime = performance.now()

			const step = (now: number) => {
				const progress = Math.min(
					1,
					(now - startTime) / ANIMATION_DURATION_MS,
				)
				const value = Math.round(
					startValue + (targetValue - startValue) * progress,
				)
				currentValueRef.current = value
				setDisplayValue(formatTotalScansCounterValue(value, locale))

				if (progress < 1) {
					animationFrameId = requestAnimationFrame(step)
				}
			}

			animationFrameId = requestAnimationFrame(step)
		}

		const tick = () => {
			animateTo(
				getTotalScansCounterValue(total, ratePerSecond, countedAt),
			)
		}

		tick()
		const interval = setInterval(tick, TICK_INTERVAL_MS)
		window.addEventListener('pageshow', tick)

		return () => {
			clearInterval(interval)
			cancelAnimationFrame(animationFrameId)
			window.removeEventListener('pageshow', tick)
		}
	}, [total, ratePerSecond, countedAt, locale])

	return (
		<span id={id} className="inline-block text-center font-semibold">
			{displayValue}
		</span>
	)
}
